<template>
  <div>
    <div class="content-body">
      <div class="row mt-5 mr-2 ml-2 justify-content-md-center">
        <h2 v-if="!initialize">Initializing App ...</h2>
        <h2
          v-if="
            initialize && (hasAccount || keyExpired || !getTaskCurrentTaskType)
          "
        >
          {{ message }}
        </h2>
      </div>
      <keep-alive>
        <component
          v-bind:is="getTaskCurrentTaskType"
          v-if="initialize"
          :link-info="linkInfo"
        ></component>
      </keep-alive>
    </div>
  </div>
</template>
<script>
import api from '../api/http-common.js'
import { mapGetters, mapActions } from 'vuex'
import '../assets/FormLayout.css'
export default {
  components: {
    // Lazy Loading components
    CreateAccount: () => import('../views/account/Create.vue'),
    PatientInfo: () => import('../components/task/PatientInfo.vue'),
    NphComplaintLocation: () =>
      import('../components/task/NphComplaintLocation.vue'),
    NphComplaintQuestions: () =>
      import('../components/task/NphComplaintQuestions.vue'),
    NphAutoAccident: () => import('../components/task/NphAutoAccident.vue'),
    NphQuestion: () => import('../components/task/NphQuestion.vue'),
    NphPersonalHistory: () =>
      import('../components/task/NphPersonalHistory.vue'),
    NphFamilyHistory: () => import('../components/task/NphFamilyHistory.vue'),
    NphThankyou: () => import('../components/task/NphThankyou.vue'),
    NphInsurance: () => import('../components/task/NphInsurance.vue'),
    ResetPassword: () => import('../components/task/ResetPassword.vue'),
    Statement: () => import('../components/billing/TaskStatement.vue')
  },
  data() {
    return {
      initialize: false,
      linkInfo: null,
      task: null,
      module: null,
      hasAccount: false,
      keyExpired: false
    }
  },
  computed: {
    ...mapGetters(['getTaskCurrentTaskType']),
    key() {
      return this.$route.query.key
    },
    message() {
      if (this.keyExpired && this.initialize) {
        return 'Link key expired. Redirecting to login page...'
      }
      if (this.initialize && this.hasAccount) {
        return 'Existing account found. Redirecting to login page...'
      }
      return ''
    }
  },
  methods: {
    ...mapActions([
      'setTaskCurrentTaskType',
      'setTaskOfficeName',
      'setTaskCode',
      'setTaskIsAccessOnly',
      'setBillingStatementTask',
      'setFacilityInfo',
      'setTaskInfo',
      'setUrlLinkInfo',
      'clearUrlLinkInfo'
    ]),
    async processTask() {
      if (this.key) {
        await this.processLinkTask()
        return
      }
      this.initialize = true
      console.log('message', this.message)
    },
    async processLinkTask() {
      try {
        // NOTE: Get Link information
        const linkResponse = await api.auth.request.getLink(this.key)
        this.linkInfo = linkResponse.data
        console.log(this.linkInfo)
        if (!this.linkInfo || this.linkInfo.expired) {
          this.initialize = true
          this.keyExpired = true
          setTimeout(() => {
            this.$router.push({
              path: '/account/login',
              query: { key: this.key }
            })
          }, 1500)
          return
        } else {
          console.log('linkResponse.data', linkResponse.data)
          await this.setUrlLinkInfo(linkResponse.data)
        }

        // Handle different task types
        if (this.linkInfo.taskType === 'RESETPWD') {
          // Password reset task - show password reset form
          this.setTaskCurrentTaskType('ResetPassword')
          this.initialize = true
          return
        } else if (this.linkInfo.taskType === 'INS') {
          // Insurance task - handle based on user existence
          if (!this.linkInfo.userExists) {
            // User doesn't exist - show account creation form
            this.setTaskCurrentTaskType('CreateAccount')
          } else {
            // User exists - set hasAccount flag and redirect to login
            this.hasAccount = true
            setTimeout(() => {
              this.$router.push({
                path: '/account/login',
                query: { key: this.key }
              })
            }, 3000)
          }
          this.initialize = true
          return
        } else {
          // For other task types, check if user exists
          if (!this.linkInfo.userExists) {
            // User doesn't exist - show account creation form
            this.setTaskCurrentTaskType('CreateAccount')
          } else {
            // User exists - set hasAccount flag and redirect to login
            this.hasAccount = true
            setTimeout(() => {
              this.$router.push({
                path: '/account/login',
                query: { key: this.key }
              })
            }, 3000)
          }
          this.initialize = true
        }
      } catch (error) {
        console.error('Error processing link task:', error)
        this.initialize = true
        this.keyExpired = true
        setTimeout(() => {
          this.$router.push({
            path: '/account/login',
            query: { key: this.key }
          })
        }, 1500)
      }
    },
    async processTasksSequentially() {
      if (this.isProcessingTasks) {
        return
      }

      this.isProcessingTasks = true

      try {
        // Check if user exists first
        if (this.linkInfo.userExists) {
          // User exists - redirect to login
          this.hasAccount = true
          setTimeout(() => {
            this.$router.push({
              path: '/account/login',
              query: { key: this.key }
            })
          }, 3000)
          return
        }

        // Fetch all tasks
        const tasks = await this.fetchTasks()

        // Get the next task in priority order
        const nextTask = this.getNextTaskInOrder(tasks)

        if (!nextTask) {
          // No tasks available - check if we need to create account
          if (!this.linkInfo.userExists) {
            this.setTaskCurrentTaskType('CreateAccount')
          }
          return
        }

        // Handle RESETPWD as a special case (it's handled via link info, not task list)
        // RESETPWD should be processed first if present in linkInfo
        if (this.linkInfo.taskType === 'RESETPWD') {
          // RESETPWD is handled via linkInfo, not task list
          // Track it as current task (using taskID from linkInfo if available)
          if (this.linkInfo.taskID) {
            this.currentTaskId = this.linkInfo.taskID
          }
          this.setTaskCurrentTaskType('ResetPassword')
          return
        }

        // Process the task
        const taskProcessed = await this.processTaskByType(nextTask)

        if (taskProcessed) {
          // For tasks that auto-complete immediately (like MSG), wait for completion
          if (nextTask.taskType === 'MSG') {
            // MSG tasks are auto-completed, so wait and then check for next task
            setTimeout(async () => {
              await this.handleTaskCompletion()
            }, 2000)
          }
          // For other tasks, wait for completion event or user interaction
          // The handleTaskCompletion will be called via events or watchers
        }
      } catch (error) {
        console.error('Error processing tasks sequentially:', error)
      } finally {
        this.isProcessingTasks = false
      }
    },
    async checkAndProcessNextTask() {
      if (this.isProcessingTasks || this.waitingForTaskCompletion) {
        return
      }

      this.isProcessingTasks = true

      try {
        // Fetch tasks again to get updated list
        const tasks = await this.fetchTasks()

        // Verify current task is actually completed (not in the list anymore)
        if (this.currentTaskId) {
          const currentTaskStillExists = tasks.some(
            task => task.taskID === this.currentTaskId
          )
          if (currentTaskStillExists) {
            // Current task is still in the list, not completed yet
            console.log('Current task not yet completed, waiting...')
            this.isProcessingTasks = false
            return
          }
          // Current task is completed, clear it
          this.currentTaskId = null
        }

        // Get the next task in priority order
        const nextTask = this.getNextTaskInOrder(tasks)

        if (nextTask) {
          // Process the next task
          await this.processTaskByType(nextTask)
        } else {
          // NOTE: If user does not exist, set to create account otherwise redirect to login
          if (!this.linkInfo || !this.linkInfo.userExists) {
            // User doesn't exist in the system - show account creation form
            this.setTaskCurrentTaskType('CreateAccount')
          } else {
            // User exists - set hasAccount flag and redirect to login
            this.hasAccount = true
            setTimeout(() => {
              this.$router.push({
                path: '/account/login',
                query: { key: this.key }
              })
            }, 3000)
          }
        }
      } catch (ex) {
        console.log('ex', ex)
      } finally {
        this.initialize = true
      }
    },
    async processDirectTask() {}
  },
  async mounted() {
    await this.processTask()
  },
  beforeDestroy() {
    // Clear URL link info when leaving task layout
    this.clearUrlLinkInfo()
  }
}
</script>
